import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import styled, { ThemeProvider } from "styled-components"
import { Normalize } from "styled-normalize"
import Footer from "../navigation/containers/footer/footer.container"
import theme from "../theme"
import { PromoBar } from "../ui/"
import BackToTop from "./common/back-to-top.component"
import {
  commerceGridSpacing,
  gridSpacing,
} from "./common/general-styles.component"
import Banner, { BannerProps } from "./common/layout/banner.component"
import Header from "../navigation/containers/header/header.container"
import SkipLinksNavigation, {
  contentStartId,
} from "./common/skip-links-navigation.component"
import GlobalStyle from "./global-style.component"
import TranslateContentWrapper from "../helpers/translate-content-wrapper"
import { AppFunctionComponent, TextBuilder } from "../types"
import { SubmenuItem } from "../navigation/types/menu.interface"

interface Props {
  masterPage?: boolean
  withSmoothScroll?: boolean
  withBackToTop?: boolean
  commerceCheckout?: boolean
  landing?: boolean
  isSleepBetter?: boolean
}

export const Main = styled.main<Props>`
  --grid-spacing: "50px";
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: 100%;
  ${({ masterPage }) => !masterPage && gridSpacing};
  ${({ commerceCheckout }) => commerceCheckout && commerceGridSpacing};
  grid-row-gap: var(--grid-spacing);

  @media (min-width: 960px) {
    padding-top: 0;
    margin-top: ${({ landing }) => (landing ? "0" : "15px")};
  }
`

interface LayoutProps extends Props {
  topNavigationItems: SubmenuItem[]
  footerNavigationItems: SubmenuItem[]
  promobarContent: TextBuilder
  darkMode?: boolean
  withoutFooter?: boolean
  smallHeader?: boolean
}
interface DataQuery {
  site: {
    siteMetadata: {
      title: string
    }
  }

  banner?: BannerProps
}

const Layout: AppFunctionComponent<LayoutProps> = ({
  children,
  masterPage,
  withBackToTop,
  commerceCheckout,
  topNavigationItems,
  footerNavigationItems,
  promobarContent,
  landing,
  darkMode,
  withoutFooter,
  isSleepBetter,
  smallHeader,
}) => {
  const { site, banner } = useStaticQuery<DataQuery>(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
      banner: contentfulSection(contentfulid: { eq: "breath-banner" }) {
        text {
          raw
        }
      }
    }
  `)

  if (
    typeof window !== "undefined" &&
    !window?.location?.href.includes("/community/blog")
  ) {
    return (
      <ThemeProvider theme={theme}>
        <TranslateContentWrapper>
          <Normalize />
          <GlobalStyle $isDark={darkMode} />
          <SkipLinksNavigation />
          {banner && <Banner text={banner.text} />}
          {!landing && !isSleepBetter && <PromoBar content={promobarContent} />}
          <Header
            navigationItems={topNavigationItems}
            siteTitle={site.siteMetadata.title}
            isDark={darkMode}
            smallHeader={smallHeader}
          />
          <Main
            masterPage={masterPage}
            commerceCheckout={commerceCheckout}
            id={contentStartId}
            landing={landing || smallHeader}
          >
            {children}
          </Main>
          {!withoutFooter && <Footer navigationItems={footerNavigationItems} />}
          {withBackToTop && <BackToTop />}
        </TranslateContentWrapper>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Normalize />
      <GlobalStyle $isDark={darkMode} />
      <SkipLinksNavigation />
      {banner && <Banner text={banner.text} />}
      {(!landing || !isSleepBetter) && <PromoBar content={promobarContent} />}
      <Header
        navigationItems={topNavigationItems}
        siteTitle={site.siteMetadata.title}
        isDark={darkMode}
        smallHeader={smallHeader}
      />
      <Main
        masterPage={masterPage}
        commerceCheckout={commerceCheckout}
        id={contentStartId}
        landing={landing || smallHeader}
      >
        {children}
      </Main>
      {!withoutFooter && <Footer navigationItems={footerNavigationItems} />}
      {withBackToTop && <BackToTop />}
    </ThemeProvider>
  )
}

export default Layout
